'use client';

import { fetchJson } from '@/lib/api/client';
import type { PooledDialerMembershipResponse, PooledDialerStatusResponse } from '../../../../shared/types/pooled-dialer';

type StatusPayload = { ok: true } & PooledDialerStatusResponse;
type MembershipPayload = { ok: true } & PooledDialerMembershipResponse;

const toStatus = ({ pool, settings, dueCallbacks }: StatusPayload): PooledDialerStatusResponse =>
  ({ pool, settings, dueCallbacks });

export const pooledDialerApi = {
  getStatus(campaignId: string): Promise<PooledDialerStatusResponse> {
    return fetchJson<StatusPayload>(`/api/campaigns/${campaignId}/pool/status`).then(toStatus);
  },

  join(campaignId: string): Promise<PooledDialerStatusResponse> {
    return fetchJson<StatusPayload>(`/api/campaigns/${campaignId}/pool/join`, { method: 'POST' }).then(toStatus);
  },

  // onlyIfParked: the server refuses (409) when a call has connected since the
  // client last looked — used before a self-reload so it never drops a live call.
  leave(campaignId: string, options: { onlyIfParked?: boolean } = {}): Promise<PooledDialerStatusResponse> {
    return fetchJson<StatusPayload>(`/api/campaigns/${campaignId}/pool/leave`, {
      method: 'POST',
      body: JSON.stringify(options),
    }).then(toStatus);
  },

  myMembership(): Promise<PooledDialerMembershipResponse> {
    return fetchJson<MembershipPayload>('/api/pooled-dialer/mine').then(
      ({ membership, serverAt, restarting, restartHandoff }) => ({
        membership,
        serverAt,
        restarting,
        restartHandoff,
      })
    );
  },

  finishWrapup(campaignId: string): Promise<PooledDialerMembershipResponse['membership']> {
    return fetchJson<MembershipPayload>(`/api/campaigns/${campaignId}/pool/wrapup-finish`, { method: 'POST' }).then(
      ({ membership }) => membership
    );
  },

  extendWrapup(campaignId: string): Promise<PooledDialerMembershipResponse['membership']> {
    return fetchJson<MembershipPayload>(`/api/campaigns/${campaignId}/pool/wrapup-extend`, { method: 'POST' }).then(
      ({ membership }) => membership
    );
  },

  rejoinFromIdle(campaignId: string): Promise<PooledDialerMembershipResponse['membership']> {
    return fetchJson<MembershipPayload>(`/api/campaigns/${campaignId}/pool/idle-rejoin`, { method: 'POST' }).then(
      ({ membership }) => membership
    );
  },

  // Countdown hold while the booking modal is open. Fired blindly on modal
  // open/close — the server no-ops unless a wrap-up countdown is running.
  setWrapupFrozen(campaignId: string, frozen: boolean): Promise<void> {
    return fetchJson<{ ok: true }>(`/api/campaigns/${campaignId}/pool/wrapup-freeze`, {
      method: 'POST',
      body: JSON.stringify({ frozen }),
    }).then(() => undefined);
  },

  // The reloaded tab taking its live call back: 409 while the call cannot be
  // carried over (yet).
  claimRescue(campaignId: string, legId: string): Promise<PooledDialerMembershipResponse['membership']> {
    return fetchJson<MembershipPayload>(`/api/campaigns/${campaignId}/pool/rescue`, {
      method: 'POST',
      body: JSON.stringify({ legId }),
    }).then(({ membership }) => membership);
  },

  finishManualWork(campaignId: string): Promise<PooledDialerMembershipResponse['membership']> {
    return fetchJson<MembershipPayload>(`/api/campaigns/${campaignId}/pool/manual-finish`, { method: 'POST' }).then(
      ({ membership }) => membership
    );
  },
};
